import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Menu, X, LogOut, User as UserIcon, Plus, Bell, MessageCircle, Settings, ShieldCheck, BadgeCheck, Shield, Sun, Moon, FileText } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import VipBadge from "./VipBadge";
import TokenBalance from "./TokenBalance";
import LanguageSwitcher from "./LanguageSwitcher";
import PostContextSwitcher from "./PostContextSwitcher";
import { useTheme } from "../contexts/ThemeContext";

const useNavLinks = () => {
  const { t } = useTranslation();
  // Veyl vises i hovedmenyen i 72 timer for trafikkmåling.
  // Etter denne datoen vises den kun i footer + banner på skapere-siden.
  const VEYL_NAV_EXPIRES = new Date("2026-07-20T17:48:00Z");
  const showVeyl = new Date() < VEYL_NAV_EXPIRES;
  return [
    { to: "/forum",        label: t("header.forum") },
    { to: "/chat",         label: t("header.chat") },
    { to: "/bilder",       label: t("header.gallery") },
    { to: "/noveller",     label: t("header.novellas") },
    { to: "/grupper",      label: t("header.groups") },
    { to: "/annonser",     label: t("header.ads") },
    { to: "/sok",          label: t("header.search") },
    { to: "/skapere",      label: t("header.creators") },
    ...(showVeyl ? [{ to: "/veyl?utm_source=header&utm_campaign=veyl_launch", label: "Veyl", highlight: true, testidKey: "veyl" }] : []),
    { to: "/konkurranser", label: t("header.contests") },
    { to: "/online",       label: t("header.online") },
  ];
};

const navLinks = [
  { to: "/forum",    label: "Forum" },
  { to: "/chat",     label: "Felleschat" },
  { to: "/bilder",   label: "Galleri" },
  { to: "/noveller", label: "Noveller" },
  { to: "/grupper",  label: "Grupper" },
  { to: "/annonser", label: "Annonser" },
  { to: "/sok",      label: "Søk" },
  { to: "/skapere",  label: "Skapere" },
  { to: "/konkurranser", label: "Konkurranser" },
  { to: "/online",   label: "Online" },
];

export default function Header() {
  const { user, logout, linkedCouple } = useAuth();
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navLinks = useNavLinks();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const [unreadDm, setUnreadDm] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);

  useEffect(() => {
    if (!user || user === false) return;
    const fetchCounts = () => {
      // Samlet innboks-tall: DM + pending guestbook + pending mentions (respekterer brukerens prefs)
      api.get("/inbox/summary").then((r) => setUnreadDm(r.data.total || 0)).catch(() => {});
      api.get("/notifications/unread-count").then((r) => setUnreadNotifs(r.data.unread || 0)).catch(() => {});
      api.get("/chat/unread-summary").then((r) => setUnreadChat(r.data.unread || 0)).catch(() => {});
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 15000);
    return () => clearInterval(t);
  }, [user]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setMobileOpen(false);
    }
  };

  // Sync server-stored ui_language til i18next KUN ved første innlogging.
  // Etter det styrer LanguageSwitcher språket lokalt + sender til server.
  const langSyncedRef = React.useRef(false);
  useEffect(() => {
    if (langSyncedRef.current) return;
    if (user && user !== false && user.ui_language && user.ui_language !== i18n.language) {
      i18n.changeLanguage(user.ui_language);
    }
    if (user && user !== false) {
      langSyncedRef.current = true;
    }
  }, [user, i18n]);

  const initials = user && user.username ? user.username.slice(0, 2).toUpperCase() : "U";

  return (
    <header className="sticky z-40 border-b" style={{ top: "var(--beta-banner-h, 0px)", background: "var(--noir)", borderColor: "rgba(212, 165, 116, 0.7)" }} data-testid="site-header">
      <div className="w-full px-2 sm:px-3 lg:px-4 h-16 sm:h-20 flex items-center gap-1.5 sm:gap-2 overflow-hidden">
        {/* Tema-bryter helt til venstre, foran logoen */}
        <button
          type="button"
          onClick={toggleTheme}
          className="hidden sm:flex shrink-0 h-8 w-8 items-center justify-center rounded-sm border border-white/10 text-[var(--cream-soft)] hover:text-[var(--magenta)] hover:border-[var(--magenta)]/50 transition-colors"
          aria-label={theme === "light" ? "Bytt til mørkt tema" : "Bytt til lyst tema"}
          title={theme === "light" ? "Bytt til mørkt tema" : "Bytt til lyst tema"}
          data-testid="theme-toggle"
        >
          {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </button>
        <Link to="/" className="flex flex-col leading-none shrink-0 min-w-0" data-testid="brand-link">
          <span className="brand-text text-base sm:text-xl lg:text-xl xl:text-2xl whitespace-nowrap">Norsk Erotikk</span>
          <span className="font-meta text-[9px] tracking-[0.3em] text-[var(--cream-faint)] mt-0.5">18+</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0">
          {navLinks.map((l) => {
            const pathOnly = l.to.split("?")[0];
            const showChatBadge = pathOnly === "/chat" && unreadChat > 0;
            return (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${pathOnly.slice(1)}`}
              data-tour={`nav-${pathOnly.slice(1).split("/")[0]}`}
              className={({ isActive }) =>
                `relative px-1 xl:px-1.5 py-2 font-manrope text-[10px] xl:text-[10.5px] uppercase tracking-[0.08em] xl:tracking-[0.12em] font-semibold transition-colors whitespace-nowrap ${
                  l.highlight
                    ? "text-[var(--magenta)] animate-pulse"
                    : isActive
                    ? "text-[var(--magenta)]"
                    : "text-[var(--cream)] hover:text-[var(--magenta)]"
                }`
              }
            >
              {l.label}
              {l.highlight && (
                <span className="ml-1 inline-block align-middle text-[7px] tracking-[0.15em] bg-[var(--magenta)] text-white px-1 py-[1px] rounded-sm">NYTT</span>
              )}
              {showChatBadge && (
                <span
                  data-testid="nav-chat-unread"
                  className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[var(--magenta)] text-black text-[9px] font-bold align-middle"
                >
                  {unreadChat > 9 ? "9+" : unreadChat}
                </span>
              )}
            </NavLink>
            );
          })}
        </nav>

        <div className="ml-2 lg:ml-2 flex items-center gap-1 sm:gap-1.5 shrink-0">
          {user && user !== false ? (
            <>
              <Link
                to="/meldinger"
                className="relative p-2 text-[var(--cream-soft)] hover:text-[var(--cream)] transition-colors"
                data-testid="header-meldinger"
                data-tour="notifs"
                title="Meldinger"
              >
                <MessageCircle className="h-4 w-4" />
                {unreadDm > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-[var(--magenta)] text-black text-[9px] font-bold flex items-center justify-center px-1">
                    {unreadDm > 9 ? "9+" : unreadDm}
                  </span>
                )}
              </Link>
              <Link
                to="/varsler"
                className="relative p-2 text-[var(--cream-soft)] hover:text-[var(--cream)] transition-colors"
                data-testid="header-varsler"
                title="Varsler"
              >
                <Bell className="h-4 w-4" />
                {unreadNotifs > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-[var(--magenta)] text-black text-[9px] font-bold flex items-center justify-center px-1">
                    {unreadNotifs > 9 ? "9+" : unreadNotifs}
                  </span>
                )}
              </Link>
              <span className="hidden sm:inline-flex"><TokenBalance /></span>
              <Link
                to="/vip"
                data-testid="header-cta-bli-vip"
                className={`inline-flex items-center whitespace-nowrap rounded-sm font-manrope text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-bold px-1.5 sm:px-2 py-1 transition-opacity hover:opacity-90 ${
                  user?.is_vip
                    ? "bg-[var(--gold)]/15 text-[var(--gold)] border border-[var(--gold)]/60"
                    : "bg-[var(--magenta)] text-black"
                }`}
                title={user?.is_vip ? "VIP" : "Bli VIP"}
              >
                {user?.is_vip ? "✅ VIP" : "✨ Bli VIP"}
              </Link>
              <span className="hidden xl:inline-flex"><PostContextSwitcher /></span>
              <span className="hidden sm:inline-flex"><LanguageSwitcher variant="header" /></span>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3" data-testid="user-menu-trigger" data-tour="profile-menu">
                  <Avatar className="h-8 w-8 rounded-sm border border-white/10">
                    <AvatarFallback className="bg-[var(--magenta)] text-black text-[10px] font-bold rounded-sm">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:flex items-center gap-1.5 font-fraunces italic text-[var(--cream)] text-sm lg:text-base max-w-[120px] lg:max-w-[160px] truncate">
                    <span className="truncate">{user.username}</span>
                    <VipBadge user={user} size="sm" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[var(--noir-elev)] border-white/10 text-[var(--cream)] w-56 rounded-sm">
                <DropdownMenuLabel className="font-fraunces italic text-base">{user.username}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />

                {/* 1. Min profil */}
                <DropdownMenuItem asChild>
                  <Link to={`/profile/${user.username}`} data-testid="menu-my-profile" className="font-manrope text-xs uppercase tracking-[0.15em]">
                    <UserIcon className="h-3.5 w-3.5 mr-2" /> {t("header.my_profile")}
                  </Link>
                </DropdownMenuItem>

                {/* 2. Min skaperside (kun skaper) */}
                {user?.is_creator && (
                  <DropdownMenuItem asChild>
                    <Link to="/min-skaperside" data-testid="menu-min-skaperside" className="font-manrope text-xs uppercase tracking-[0.15em]">
                      <Plus className="h-3.5 w-3.5 mr-2" /> {t("header.my_creator_page")}
                    </Link>
                  </DropdownMenuItem>
                )}

                {/* 3. Skaper-innboks (kun skaper) */}
                {user?.is_creator && (
                  <DropdownMenuItem asChild>
                    <Link to="/meldinger?context=creator" data-testid="menu-creator-inbox" className="font-manrope text-xs uppercase tracking-[0.15em] text-[var(--magenta)]">
                      <MessageCircle className="h-3.5 w-3.5 mr-2" /> {t("header.creator_inbox")}
                    </Link>
                  </DropdownMenuItem>
                )}

                {/* 4. Par-profil (kun par) */}
                {linkedCouple && (
                  <DropdownMenuItem asChild>
                    <Link to="/par-innboks" data-testid="menu-par-inbox" className="font-manrope text-xs uppercase tracking-[0.15em] text-[var(--magenta)]">
                      <MessageCircle className="h-3.5 w-3.5 mr-2" /> Par-profil
                    </Link>
                  </DropdownMenuItem>
                )}

                {/* 5. Innstillinger */}
                <DropdownMenuItem asChild>
                  <Link to="/innstillinger" data-testid="menu-settings" className="font-manrope text-xs uppercase tracking-[0.15em]">
                    <Settings className="h-3.5 w-3.5 mr-2" /> {t("header.settings")}
                  </Link>
                </DropdownMenuItem>

                {/* 5b. Kvitteringer */}
                <DropdownMenuItem asChild>
                  <Link to="/kvitteringer" data-testid="menu-kvitteringer" className="font-manrope text-xs uppercase tracking-[0.15em]">
                    <FileText className="h-3.5 w-3.5 mr-2" /> Kvitteringer
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-white/10" />

                {/* Status-rader: Verifisert / Selger (VIP flyttet til synlig header-CTA) */}
                <DropdownMenuItem asChild>
                  <Link to="/lommebok" data-testid="menu-verifisert" className={`font-manrope text-xs uppercase tracking-[0.15em] ${(user?.is_verified || (user?.verification_level || 0) >= 1) ? "text-emerald-400" : ""}`}>
                    <ShieldCheck className="h-3.5 w-3.5 mr-2" />
                    {(user?.is_verified || (user?.verification_level || 0) >= 1) ? "Verifisert" : "Få verifisert konto"}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to={user?.seller_verified ? "/selger" : "/innstillinger/selger-verifisering"}
                    data-testid="menu-bli-selger"
                    className={`font-manrope text-xs uppercase tracking-[0.15em] ${user?.seller_verified ? "text-emerald-400" : ""}`}
                  >
                    <BadgeCheck className="h-3.5 w-3.5 mr-2" />
                    {user?.seller_verified ? "Selger" : "Bli selger"}
                  </Link>
                </DropdownMenuItem>

                {/* --- Admin --- */}
                {["owner", "admin", "staff"].includes(user?.role) && (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem asChild>
                      <Link to="/admin/board" data-testid="menu-admin-board" className="font-manrope text-xs uppercase tracking-[0.15em] text-amber-400">
                        <Shield className="h-3.5 w-3.5 mr-2" /> {t("header.admin_board")}
                      </Link>
                    </DropdownMenuItem>
                    {["owner", "admin"].includes(user.role) && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/moderasjon" data-testid="menu-moderation" className="font-manrope text-xs uppercase tracking-[0.15em] text-[var(--magenta)]">
                            <ShieldCheck className="h-3.5 w-3.5 mr-2" /> {t("header.moderation")}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/statistikk" data-testid="menu-statistikk" className="font-manrope text-xs uppercase tracking-[0.15em] text-[var(--magenta)]">
                            <ShieldCheck className="h-3.5 w-3.5 mr-2" /> {t("header.statistics")}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                )}

                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  className="text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 text-xs uppercase tracking-[0.15em]"
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="menu-logout"
                >
                  <LogOut className="h-3.5 w-3.5 mr-2" /> {t("header.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <>
              <LanguageSwitcher variant="header" />
              <Link to="/login" className="hidden sm:inline-block font-manrope text-[11px] uppercase tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--cream)]" data-testid="login-button">
                {t("header.login")}
              </Link>
              <Link to="/register" className="btn-magenta inline-block whitespace-nowrap text-[10px] sm:text-xs px-3 sm:px-4" data-testid="register-button">
                {t("header.join")}
              </Link>
            </>
          )}

          <button
            className="lg:hidden p-2 text-[var(--cream)]"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/8 bg-[var(--noir)]" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block px-2 py-3 font-manrope text-xs uppercase tracking-[0.2em] ${
                    l.highlight
                      ? "text-[var(--magenta)]"
                      : isActive
                      ? "text-[var(--magenta)]"
                      : "text-[var(--cream-soft)]"
                  }`
                }
              >
                {l.label}
                {l.highlight && (
                  <span className="ml-2 inline-block align-middle text-[8px] tracking-[0.15em] bg-[var(--magenta)] text-white px-1.5 py-[1px] rounded-sm normal-case">NYTT</span>
                )}
                {l.to.split("?")[0] === "/chat" && unreadChat > 0 && (
                  <span
                    data-testid="nav-chat-unread-mobile"
                    className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[var(--magenta)] text-black text-[10px] font-bold align-middle"
                  >
                    {unreadChat > 9 ? "9+" : unreadChat}
                  </span>
                )}
              </NavLink>
            ))}
            <form onSubmit={onSearch} className="pt-3 px-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("common.search")}
                className="input-editorial text-xs uppercase tracking-[0.15em]"
              />
            </form>
            <div className="pt-3 px-2 flex items-center justify-between gap-3">
              <LanguageSwitcher variant="full" />
              <button
                type="button"
                onClick={toggleTheme}
                className="shrink-0 h-9 w-9 flex items-center justify-center rounded-sm border border-white/10 text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                aria-label={theme === "light" ? "Bytt til mørkt tema" : "Bytt til lyst tema"}
                data-testid="theme-toggle-mobile"
              >
                {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
            </div>
            {(!user || user === false) && (
              <div className="flex gap-3 pt-4 px-2">
                <Link to="/login" className="btn-ghost flex-1 text-center" onClick={() => setMobileOpen(false)}>{t("header.login")}</Link>
                <Link to="/register" className="btn-magenta flex-1 text-center" onClick={() => setMobileOpen(false)}>{t("header.join")}</Link>
              </div>
            )}
            {user && user !== false && !user.is_vip && (
              <div className="flex gap-3 pt-4 px-2">
                <Link
                  to="/vip"
                  data-testid="mobile-cta-bli-vip"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 text-center rounded-sm bg-[var(--magenta)] text-black font-manrope text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-2"
                >
                  ✨ Bli VIP
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
